import React, { useContext, useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserPlus, faClock, faCog } from '@fortawesome/free-solid-svg-icons';
import { WebSocketContext } from './WebSocketProvider';
import LoadingSpinner from './LoadingSpinner';
import { Buffer } from 'buffer';
import './MessageList.css';
import CountrySelect from './CountrySelect';
import ChatConfigModal from './ChatConfigModal';
// import { clear } from 'console';

const MessageList = forwardRef(({ sessionId, currentThreadId, userId, contacts, sendInvite, threads, isLoadingThread }, ref) => {
    const { messages, setMessages, imageFetch } = useContext(WebSocketContext);
    const messageListRef = useRef();
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [showConfigModal, setShowConfigModal] = useState(false);
    const [inviteePhone, setInviteePhone] = useState('');
    const [inviteeCountryCode, setInviteeCountryCode] = useState('+1');
    const [fullscreenImage, setFullscreenImage] = useState(null);
    const [fullscreenImageId, setFullscreenImageId] = useState(null);
    const [securityTooltip, setSecurityTooltip] = useState({ show: false, x: 0, y: 0 });
    const [expirationTimers, setExpirationTimers] = useState({});

    // Add global event listener to prevent context menu on images
    useEffect(() => {
        const preventImageContextMenu = (e) => {
            // If the right-clicked element is an image or has an image parent
            if (e.target.tagName === 'IMG' || e.target.querySelector('img') || e.target.closest('.image-modal')) {
                e.preventDefault();

                // Show tooltip near the cursor, but with vertical offset to ensure visibility
                setSecurityTooltip({
                    show: true,
                    x: e.clientX,
                    y: e.clientY - 20 // Add a 20px offset to position it above cursor
                });

                // Hide tooltip after 2 seconds
                setTimeout(() => {
                    setSecurityTooltip({ show: false, x: 0, y: 0 });
                }, 2000);

                return false;
            }
        };

        // Add the event listener to the window
        window.addEventListener('contextmenu', preventImageContextMenu);

        // Clean up when component unmounts
        return () => {
            window.removeEventListener('contextmenu', preventImageContextMenu);
        };
    }, []);

    // Add a timer to check message expiration every minute
    useEffect(() => {
        // Update expiration counters and hide expired messages
        const updateExpirationTimers = () => {
            const now = Date.now();
            const newTimers = {};

            // Get current thread settings for unhiddenInterval
            const currentThread = threads.find(thread => thread.id === currentThreadId);
            const threadInterval = currentThread?.config?.unhiddenInterval || 86400000; // Default to 24 hours

            console.log("Updating expiration timers for thread:", currentThreadId);
            //print messages for current thread
            console.log("Messages for current thread:", messages.filter(msg => msg.threadId === currentThreadId));

            console.log("Thread config:", currentThread?.config);

            // Check messages for the current thread
            messages.forEach(msg => {
                if (msg.threadId === currentThreadId && msg.timestamp) {
                    // Calculate when this message will expire based on thread settings
                    // if message-specific autoHideInterval is set, use the smaller of the two

                    let interval = threadInterval;

                    if (msg.autoHideInterval) {
                        interval = Math.min(interval, msg.autoHideInterval);
                    }

                    // Convert timestamp to milliseconds if it's not already
                    const msgTimestamp = new Date(msg.timestamp).getTime();
                    const expiresAt = msgTimestamp + interval;

                    // Calculate time left
                    const timeLeft = expiresAt - now;

                    if (timeLeft > 0) {
                        // Format time left to display
                        if (timeLeft > 86400000) { // More than 1 day
                            const daysLeft = Math.floor(timeLeft / 86400000);
                            newTimers[msg.id] = `${daysLeft}d`;
                        } else if (timeLeft > 3600000) { // More than 1 hour
                            const hoursLeft = Math.floor(timeLeft / 3600000);
                            const minutesLeft = Math.floor((timeLeft % 3600000) / 60000);
                            newTimers[msg.id] = `${hoursLeft}h ${minutesLeft > 0 ? minutesLeft + 'm' : ''}`;
                        } else if (timeLeft > 300000) { // More than 5 minutes
                            const minutesLeft = Math.floor(timeLeft / 60000);
                            newTimers[msg.id] = `${minutesLeft}m`;
                        } else if (timeLeft > 60000) { // Between 1-5 minutes - mark as expiring soon
                            const minutesLeft = Math.floor(timeLeft / 60000);
                            // Store the time and a flag to indicate it's expiring soon
                            newTimers[msg.id] = `${minutesLeft}m`;
                        } else { // Less than 1 minute
                            newTimers[msg.id] = `<1m`;
                        }
                    }
                }
            });

            console.log("New expiration timers:", newTimers);
            // Update the expiration timers
            setExpirationTimers(newTimers);
        };

        // Run immediately and then every 30 seconds
        updateExpirationTimers();
        const intervalId = setInterval(updateExpirationTimers, 30000);

        return () => clearInterval(intervalId);
    }, [messages, currentThreadId, threads]);

    const clearMessages = () => {
        setMessages([]);
    };

    const closeImage = () => {
        setMessages((prev) => prev.map((msg) => {
            if (msg.imageId === fullscreenImageId) {
                return {
                    ...msg,
                    image: null,
                    placeholder: true,
                    holding: false
                };
            } else {
                return msg;
            }
        }));
        setFullscreenImage(null);
        setFullscreenImageId(null);
    }

    useImperativeHandle(ref, () => ({
        clearMessages
    }));

    // Automatically scroll to the latest message
    useEffect(() => {
        const messageList = messageListRef.current;
        if (messageList) {
            const isAtBottom =
                messageList.scrollHeight - messageList.scrollTop === messageList.clientHeight;
            if (isAtBottom) {
                messageList.scrollTop = messageList.scrollHeight;
            }
        }
    }, [messages]);

    const validatePhoneNumber = (number) => {
        // At least 3 digits after country code and no whitespace
        return number.length >= 3 && !/\s/.test(number);
    };

    const getFullInviteePhoneNumber = () => {
        // Step 1: Remove all non-digits from the phone number
        const cleanPhone = inviteePhone.replace(/\D/g, '');

        // Step 2: Ensure the country code starts with a '+'
        const cleanCountryCode = inviteeCountryCode.startsWith('+')
            ? inviteeCountryCode
            : `+${inviteeCountryCode}`;

        // Step 3: Format the phone number based on country code
        let formattedPhone = cleanPhone;

        // For US numbers (country code '+1'), format as XXX XXX XXXX
        if (cleanCountryCode === '+1' && cleanPhone.length >= 10) {
            const areaCode = cleanPhone.substring(0, 3); // First 3 digits
            const prefix = cleanPhone.substring(3, 6); // Next 3 digits
            const lineNumber = cleanPhone.substring(6, 10); // Last 4 digits
            formattedPhone = `${areaCode}${prefix}${lineNumber}`;
        }

        // Step 4: Combine country code and formatted phone number with a space
        return `${cleanCountryCode} ${formattedPhone}`.trim();
    };

    const handleInviteUser = () => {
        const fullInviteePhone = getFullInviteePhoneNumber();

        if (!validatePhoneNumber(inviteePhone)) {
            console.error('Invalid phone number');
            return;
        }
        sendInvite(fullInviteePhone, currentThreadId);
        setInviteePhone('');
        setInviteeCountryCode('+1');
        setShowInviteModal(false);
    };

    const handleImageHold = async (imageId) => {
        console.log('Fetching image...');
        setMessages((prev) => prev.map((msg) => {
            if (msg.imageId === imageId) {
                return { ...msg, holding: true };
            }
            return msg;
        }));
        // imageFetch(imageId);

        try {
            const imageBuffer = await imageFetch(imageId);
            const imageBase64 = Buffer.from(imageBuffer).toString('base64');
            setFullscreenImage(`data:image/jpeg;base64,${imageBase64}`);
            setFullscreenImageId(imageId);
            setMessages((prev) => prev.map((msg) => {
                if (msg.imageId === imageId) {
                    return { ...msg, placeholder: false };
                }
                return msg;
            }));

        } catch (error) {
            console.error('Error fetching image:', error);
        }
    };

    const handleImageRelease = (imageId) => {
        console.log('Removing image from memory...');
        setFullscreenImage(null);
    };

    // Function to convert URLs in text to clickable links
    const renderTextWithLinks = (text) => {
        // If the message is hidden, return a placeholder text
        if (!text) {
            return <em className="hidden-message-text">This message is hidden</em>;
        }

        // Regular expression to match URLs
        const urlRegex = /(https?:\/\/[^\s]+)/g;

        // Split the text by URLs and map over the parts
        const parts = text.split(urlRegex);
        return parts.map((part, index) => {
            if (part.match(urlRegex)) {
                return (
                    <a
                        key={index}
                        href={part}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: 'inherit', textDecoration: 'underline' }}
                    >
                        {part}
                    </a>
                );
            }
            return part;
        });
    };

    console.log("THREADS", threads);

    if (isLoadingThread) {
        return (
            <div className="message-list-loading">
                <LoadingSpinner />
            </div>
        )
    } else {

        return (
            <div className="message-list" ref={messageListRef}>
                {/* Configuration Icon */}
                {currentThreadId && currentThreadId !== 'general' && (
                    <>
                        <FontAwesomeIcon
                            icon={faCog}
                            className="config-icon"
                            onClick={() => setShowConfigModal(true)}
                            title="Chat Configuration"
                        />
                        <FontAwesomeIcon
                            icon={faUserPlus}
                            className="add-user-icon"
                            onClick={() => setShowInviteModal(true)}
                            title="Invite User"
                        />
                    </>
                )}

                {/* Security tooltip */}
                {securityTooltip.show && (
                    <div
                        className="security-tooltip"
                        style={{
                            position: 'fixed',
                            top: `${securityTooltip.y}px`,
                            left: `${securityTooltip.x}px`,
                            transform: 'translate(-50%, -100%)',
                            background: 'rgba(0, 0, 0, 0.85)',
                            color: 'white',
                            padding: '10px 15px',
                            borderRadius: '6px',
                            zIndex: 10000,
                            fontSize: '15px',
                            fontWeight: 'bold',
                            pointerEvents: 'none',
                            boxShadow: '0 0 10px rgba(255, 255, 255, 0.4), 0 0 5px rgba(0, 0, 0, 0.5)',
                            border: '1px solid rgba(255, 255, 255, 0.2)'
                        }}
                    >
                        Image saving is disabled for security
                    </div>
                )}
                {/* Filter to only show messages for the current thread */}
                {messages
                    .filter((msg) => msg.threadId === currentThreadId) // Only show messages in the current thread
                    .slice() // Create a shallow copy to avoid mutating the original array
                    .reverse() // Reverse the order to show the latest message at the bottom
                    .map((msg, index, arr) => {
                        // Use timestamp if available, fall back to datetime for backward compatibility
                        const timeField = msg.timestamp || msg.datetime;

                        // Extract the date from the time field if available
                        const currentDate = timeField ? new Date(timeField).toDateString() : null;
                        const previousDate = index < arr.length - 1 ?
                            new Date(arr[index + 1].timestamp || arr[index + 1].datetime).toDateString() : null;

                        const shouldShowDataSeparator = currentDate !== previousDate;

                        const formattedDate = currentDate ?
                            new Date(timeField).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : null;

                        // Extract time from the time field if available
                        const formattedTime = timeField ?
                            new Date(timeField).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : null;

                        return (
                            <React.Fragment key={msg.id || msg.imageId || `${msg.threadId}-${msg.timestamp}-${index}`}>
                                <div
                                    className={`message-container ${msg.userId === userId ? 'sent' : 'received'} ${msg.hidden ? 'hidden-message' : ''}`}
                                >

                                    <div className="message-header">
                                        {msg.userId !== userId && (
                                            <div className="sender-name">{msg.sender}</div>
                                        )}
                                        {formattedTime && (
                                            <span className={`message-time ${msg.userId === userId ? 'sent-time' : 'received-time'}`}>
                                                {formattedTime}
                                            </span>
                                        )}

                                    </div>
                                    {msg.text !== undefined && (
                                        <div className={`message ${msg.userId === userId ? 'sent' : 'received'} ${msg.hidden ? 'hidden-message' : ''}`}>
                                            {renderTextWithLinks(msg.text)}
                                        </div>
                                    )}
                                    {msg.isImage && (
                                        <div
                                            className={`message image-message ${msg.userId === userId ? 'sent' : 'received'} ${msg.hidden ? 'hidden-message' : ''}`}
                                            onMouseDown={() => !msg.hidden && handleImageHold(msg.imageId)}
                                            onMouseUp={() => !msg.hidden && handleImageRelease(msg.imageId)}
                                            onTouchStart={() => !msg.hidden && handleImageHold(msg.imageId)}
                                            onTouchEnd={() => !msg.hidden && handleImageRelease(msg.imageId)}
                                            onContextMenu={(e) => e.preventDefault()}
                                            style={{ position: 'relative' }}
                                        >
                                            {msg.hidden ? (
                                                <div className="hidden-image-placeholder">
                                                    <em>Image has expired</em>
                                                </div>
                                            ) : msg.placeholder ? (
                                                <>
                                                    <img
                                                        src="/placeholder-image.jpg"
                                                        alt="Placeholder"
                                                        className="message-image"
                                                        onContextMenu={(e) => e.preventDefault()}
                                                        draggable="false"
                                                    />
                                                    {msg.holding && (
                                                        <div className="spinner-overlay">
                                                            <div className="spinner"></div>
                                                        </div>
                                                    )}
                                                </>
                                            ) : (
                                                <img
                                                    src="/placeholder-image.jpg"
                                                    alt="Placeholder"
                                                    className="message-image"
                                                    onContextMenu={(e) => e.preventDefault()}
                                                    draggable="false"
                                                />
                                            )}
                                        </div>
                                    )}
                                    <div className="message-footer">


                                        {/* Show expiration countdown if available */}
                                        {expirationTimers[msg.id] && (
                                            <span className={`expiration-timer ${expirationTimers[msg.id] === '<1m' ||
                                                (expirationTimers[msg.id].endsWith('m') &&
                                                    parseInt(expirationTimers[msg.id]) < 5)
                                                ? 'expiring-soon' : ''}`}>
                                                <FontAwesomeIcon icon={faClock} className="timer-icon" />
                                                {expirationTimers[msg.id]}
                                            </span>
                                        )}


                                    </div>
                                </div>

                                {shouldShowDataSeparator && formattedDate && (
                                    <div className="date-separator">
                                        <span>{formattedDate}</span>
                                    </div>
                                )}
                            </React.Fragment>
                        )
                    })}
                {/* Check if current thread has hidden messages */}
                {threads.find(thread => thread.id === currentThreadId)?.hasHiddenMessages && (
                    <div className="hidden-messages-separator">
                        <span>Previous messages are hidden</span>
                    </div>
                )}

                {fullscreenImage && (
                    <div
                        className="image-modal"
                        onClick={closeImage}
                        onContextMenu={(e) => e.preventDefault()}
                    >
                        <img
                            src={fullscreenImage}
                            alt="Full-screen preview"
                            className="fullscreen-image"
                            onContextMenu={(e) => e.preventDefault()}
                            draggable="false"
                        />
                    </div>
                )}
                {/* Chat Configuration Modal */}
                {
                    <ChatConfigModal
                        isOpen={showConfigModal}
                        onClose={() => setShowConfigModal(false)}
                        currentThreadId={currentThreadId}
                        threads={threads}
                        sessionId={sessionId}
                    />
                }

                {/* Invite Modal */}
                {showInviteModal && (
                    <div className="modal-backdrop" onClick={() => setShowInviteModal(false)}>
                        <div className="modal" onClick={(e) => e.stopPropagation()}>
                            <h3>Invite to Tribe</h3>
                            <div className="phone-input-container">
                                <div className="country-select-container">
                                    <CountrySelect
                                        value={inviteeCountryCode}
                                        onChange={(value) => setInviteeCountryCode(value)}
                                    />
                                </div>
                                <input
                                    type="tel"
                                    value={inviteePhone}
                                    onChange={(e) => setInviteePhone(e.target.value.replace(/\D/g, ''))}
                                    placeholder="Invitee's phone number"
                                    className="phone-input"
                                />
                            </div>

                            {!validatePhoneNumber(inviteePhone) && inviteePhone && (
                                <p className="error-message">Entry not valid</p>
                            )}
                            <div className="modal-actions">
                                <button onClick={handleInviteUser} disabled={!validatePhoneNumber(inviteePhone)}>
                                    Send
                                </button>
                                <button onClick={() => setShowInviteModal(false)}>Cancel</button>
                            </div>
                        </div>
                    </div>
                )}

            </div>
        );
    }
});

export default MessageList;

